import React from 'react';

import { useActiveBrand, useBrandUrls } from '@/brand';
import { useLocale, useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { trpc } from '@/trpc/client';
import { ErrorMessage, TranslatedErrorMessage } from '@/ui/controls';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall, Heading, Text } from '@/ui/typography';

import { ManageBookingRequestForm } from '../components/ManageBookingRequestForm.component';

interface ManageBookingRequestFlowProps {
  onSuccess: (email: string) => Promise<void>;

  /**
   * If set to true, we render a message informing the user that their session has expired.
   */
  didSessionExpire?: boolean;
}

export const ManageBookingRequestFlow: React.FC<
  ManageBookingRequestFlowProps
> = (props) => {
  const brandReferenceId = useActiveBrand();
  const { mutate, error, isLoading } =
    trpc.auth.requestOneTimePasscode.useMutation({
      async onSuccess(result) {
        await props.onSuccess(result.email);
      },
    });
  const locale = useLocale().baseName;

  const {
    componentProperties: { bookingManagement },
  } = useTheme();

  const { t } = useTranslation('bookingManagement');
  const urls = useBrandUrls();

  return (
    <div>
      <Heading
        as="h1"
        style="heading4"
        css={{
          ...bookingManagement?.title,
          ['@media all and (max-width: 730px)']:
            bookingManagement?.title?.mobile,
        }}
      >
        {t('amendBookingRequest.title')}
      </Heading>
      {t('amendBookingRequest.subtitle') ? (
        <>
          <Spacer s="s" />
          <Heading
            as="h2"
            style="heading5"
            css={{
              ...bookingManagement?.subTitle,
              '@media all and (max-width: 730px)':
                bookingManagement?.subTitle?.mobile,
            }}
          >
            {t('amendBookingRequest.subtitle')}
          </Heading>
        </>
      ) : null}
      <Spacer s="xs" />
      <BodySmall css={bookingManagement?.description}>
        <Markdown source={t('amendBookingRequest.form.description')} />
      </BodySmall>
      <Spacer s="s" />
      <BodySmall css={bookingManagement?.description}>
        <Markdown
          source={t('amendBookingRequest.form.getInTouch', {
            contactUrl: urls?.contact,
          })}
        />
      </BodySmall>
      {props.didSessionExpire && (
        <>
          <Spacer s="s" />
          <TranslatedErrorMessage dictionaryKey="unauthorized" />
        </>
      )}
      <Spacer s="xl" />
      <ManageBookingRequestForm
        onSubmit={({ email }) => mutate({ brandReferenceId, email, locale })}
        isSubmitting={isLoading}
      />

      {error ? (
        <>
          <Spacer s="m" />
          <ErrorMessage message={error.message} />
          {/* <TranslatedErrorMessage dictionaryKey={error.message} /> */}
        </>
      ) : undefined}
    </div>
  );
};
